import React from 'react'
import { Button, Spinner } from 'react-bootstrap'
import styled from 'styled-components'
import L from 'leaflet'
import type { MapTool } from './MapToolbar'
import type { LayerInfo } from './types'

// Styled components
const InferenceToolMapPanelContainer = styled.div<{ hidden?: boolean }>`
  position: absolute;
  top: 80px;
  left: 10px;
  bottom: 50px;
  z-index: 1000;
  background-color: ${(props) => props.theme.colors.background.dark || '#1a1a1a'};
  background-color: rgba(26, 26, 26, 0.9);
  border-radius: 8px;
  padding: 12px;
  width: 300px;
  color: white;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  overflow-y: auto;
  backdrop-filter: blur(5px);
  transition: all 0.3s ease;
  transform: ${(props) => (props.hidden ? 'translateX(-110%)' : 'translateX(0)')};

  &:hover {
    background-color: rgba(26, 26, 26, 0.95);
  }

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.2);
    border-radius: 4px;

    &:hover {
      background-color: rgba(255, 255, 255, 0.3);
    }
  }

  .layer-item {
    background-color: rgba(255, 255, 255, 0.1);
    border-radius: 6px;
    margin-bottom: 8px;
    padding: 8px;
  }
`

const InferenceInstructionCard = styled.div`
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(59, 130, 246, 0.05));
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  animation: subtle-pulse 3s ease-in-out infinite;

  @keyframes subtle-pulse {
    0%,
    100% {
      border-color: rgba(59, 130, 246, 0.3);
      box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.1);
    }
    50% {
      border-color: rgba(59, 130, 246, 0.5);
      box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.05);
    }
  }

  .instruction-icon {
    text-align: center;
    margin-bottom: 16px;

    i {
      font-size: 32px;
      color: #3b82f6;
      animation: icon-glow 2s ease-in-out infinite alternate;
    }
  }

  @keyframes icon-glow {
    from {
      text-shadow: 0 0 5px rgba(59, 130, 246, 0.3);
    }
    to {
      text-shadow: 0 0 15px rgba(59, 130, 246, 0.6);
    }
  }

  .instruction-content {
    h6 {
      margin: 0 0 12px 0;
      font-size: 1.1rem;
      font-weight: 600;
      color: #3b82f6;
      text-align: center;
    }

    p {
      margin: 0 0 16px 0;
      font-size: 0.9rem;
      line-height: 1.4;
      color: rgba(255, 255, 255, 0.9);
      text-align: center;
    }
  }

  .instruction-details {
    .detail-item {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 8px;
      font-size: 0.85rem;
      color: rgba(255, 255, 255, 0.8);

      i {
        color: #10b981;
        font-size: 12px;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
`

const InferenceFileCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(59, 130, 246, 0.3);
    transform: translateY(-1px);
  }

  .file-info {
    .file-header {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 12px;

      i {
        color: #3b82f6;
        font-size: 16px;
      }

      .file-name {
        font-weight: 500;
        color: white;
        font-size: 0.9rem;
        word-break: break-word;
        flex: 1;
      }
    }

    .file-details {
      margin-bottom: 12px;

      .detail-row {
        display: flex;
        justify-content: space-between;
        margin-bottom: 4px;
        font-size: 0.85rem;

        .label {
          color: rgba(255, 255, 255, 0.7);
        }

        .value {
          color: rgba(255, 255, 255, 0.9);
          font-weight: 500;
        }
      }
    }

    .inference-status {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 12px;
      border-radius: 6px;
      font-size: 0.85rem;
      font-weight: 500;

      &.processing {
        background: rgba(59, 130, 246, 0.1);
        border: 1px solid rgba(59, 130, 246, 0.3);
        color: #3b82f6;
      }

      &.complete {
        background: rgba(16, 185, 129, 0.1);
        border: 1px solid rgba(16, 185, 129, 0.3);
        color: #10b981;
      }

      &.ready {
        background: rgba(156, 163, 175, 0.1);
        border: 1px solid rgba(156, 163, 175, 0.3);
        color: #9ca3af;
      }

      i {
        font-size: 12px;
      }
    }
  }

  .clear-button {
    position: absolute;
    top: 8px;
    right: 8px;
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    border-radius: 4px;
    padding: 4px 6px;
    color: #ef4444;
    font-size: 12px;
    cursor: pointer;
    opacity: 0;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(239, 68, 68, 0.2);
      border-color: rgba(239, 68, 68, 0.5);
    }
  }

  &:hover .clear-button {
    opacity: 1;
  }

  .click-hint {
    position: absolute;
    bottom: 8px;
    right: 8px;
    font-size: 11px;
    color: rgba(59, 130, 246, 0.7);
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover .click-hint {
    opacity: 1;
  }
`

interface InferenceToolMapPanelProps {
  layers: LayerInfo[]
  hidden: boolean
  toggleLayerVisibility: (layerId: string) => void
  removeLayer: (layerId: string) => void
  zoomToLayer: (bounds: L.LatLngBounds) => void
  handleInference: (layer: LayerInfo) => void
  isInferencing: string | null
  selectedModelId: string | null
  onTogglePanel?: () => void
  activeTool?: MapTool
}

const InferenceToolMapPanel: React.FC<InferenceToolMapPanelProps> = ({
  layers,
  hidden,
  toggleLayerVisibility,
  removeLayer,
  zoomToLayer,
  handleInference,
  isInferencing,
  selectedModelId,
  onTogglePanel,
  activeTool,
}) => {
  return (
    <InferenceToolMapPanelContainer hidden={hidden}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '10px',
        }}
      >
        <h6 style={{ margin: 0, fontSize: '1rem' }}>
          {activeTool === 'inference' ? 'Inference Mode' : 'Layers'}
        </h6>
        {onTogglePanel && (
          <Button
            variant="outline-light"
            size="sm"
            onClick={onTogglePanel}
            style={{ padding: '2px 6px' }}
          >
            <i className="bi bi-x-lg" style={{ fontSize: '12px' }}></i>
          </Button>
        )}
      </div>

      {/* Show inference instructions when inference tool is active */}
      {activeTool === 'inference' && (
        <>
          <InferenceInstructionCard>
            <div className="instruction-icon">
              <i className="bi bi-cpu"></i>
            </div>
            <div className="instruction-content">
              <h6>Ready for Inference</h6>
              <p>Drag and drop a single TIFF file anywhere on the map to run inference</p>
              <div className="instruction-details">
                <div className="detail-item">
                  <i className="bi bi-check-circle-fill"></i>
                  <span>Supports .tif and .tiff files</span>
                </div>
                <div className="detail-item">
                  <i className="bi bi-check-circle-fill"></i>
                  <span>Automatic processing and analysis</span>
                </div>
                <div className="detail-item">
                  <i className="bi bi-check-circle-fill"></i>
                  <span>Results shown on map</span>
                </div>
              </div>
            </div>
          </InferenceInstructionCard>

          {/* Show dropped file information */}
          {layers.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <h6 style={{ margin: '0 0 12px 0', fontSize: '0.95rem', color: '#3b82f6' }}>
                Uploaded File
              </h6>
              {layers.map((layer) => (
                <InferenceFileCard
                  key={layer.id}
                  onClick={() => zoomToLayer(layer.bounds)}
                  title="Click to zoom to file location"
                >
                  <button
                    className="clear-button"
                    onClick={(e) => {
                      e.stopPropagation()
                      removeLayer(layer.id)
                    }}
                    title="Remove file"
                  >
                    <i className="bi bi-x"></i>
                  </button>

                  <div className="file-info">
                    <div className="file-header">
                      <i className="bi bi-file-earmark-image"></i>
                      <span className="file-name">
                        {layer.originalFile?.name || `Layer ${layer.id}`}
                      </span>
                    </div>

                    {layer.metadata && (
                      <div className="file-details">
                        <div className="detail-row">
                          <span className="label">Dimensions:</span>
                          <span className="value">
                            {layer.metadata.width} × {layer.metadata.height}
                          </span>
                        </div>
                        {layer.gsd && (
                          <div className="detail-row">
                            <span className="label">GSD:</span>
                            <span className="value">{layer.gsd.toFixed(2)}m</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Inference status */}
                    {isInferencing === layer.id ? (
                      <div className="inference-status processing">
                        <Spinner animation="border" size="sm" />
                        <span>Running inference...</span>
                      </div>
                    ) : layer.predictions && layer.predictions.length > 0 ? (
                      <div className="inference-status complete">
                        <i className="bi bi-check-circle-fill"></i>
                        <span>{layer.predictions.length} objects detected</span>
                      </div>
                    ) : (
                      <div className="inference-status ready">
                        <i className="bi bi-clock"></i>
                        <span>Ready for inference</span>
                      </div>
                    )}
                  </div>
                </InferenceFileCard>
              ))}
            </div>
          )}
        </>
      )}
    </InferenceToolMapPanelContainer>
  )
}

export default InferenceToolMapPanel
